import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App, Card, Descriptions, Table, Button, Spin, Result, Space, Tag, Image } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { formatDate } from '../utils/format';

interface Admin {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  status: string;
  variants?: Array<{ id: string; title: string }>;
  created_at: string;
}

interface ExecutionResource {
  id: string;
  name: string;
  type: string;
  resource_number: string;
  pricing_options?: Array<{ id: string; unit: string; unit_price: number }>;
}

interface ServiceProviderData {
  id: string;
  name: string;
  handle: string;
  logo?: string;
  tin?: string;
  phone?: string;
  admins?: Admin[];
  products?: Product[];
  created_at: string;
}

const ServiceProviderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [sp, setSp] = useState<ServiceProviderData | null>(null);
  const [executionResources, setExecutionResources] = useState<ExecutionResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadServiceProvider();
  }, [id]);

  const loadServiceProvider = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/service-providers/${id}`);
      setSp(response.data.service_provider || response.data);

      try {
        const resourcesResponse = await apiClient.get('/admin/execution-resources', {
          params: { service_provider_id: id, limit: 100 },
        });
        setExecutionResources(resourcesResponse.data.execution_resources || []);
      } catch (err) {
        setExecutionResources([]);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(error.response?.data?.message || t('common.noData'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !sp) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('common.noData')}
        extra={
          <Button type="primary" onClick={() => navigate('/service-providers')}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  const adminColumns = [
    {
      title: t('customers.firstName'),
      dataIndex: 'first_name',
      key: 'first_name',
    },
    {
      title: t('customers.lastName'),
      dataIndex: 'last_name',
      key: 'last_name',
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
    },
  ];

  const productColumns = [
    {
      title: t('products.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'published' ? 'green' : 'default'}>
          {status === 'published' ? t('products.published') : t('products.draft')}
        </Tag>
      ),
    },
    {
      title: t('products.variants'),
      key: 'variants',
      render: (_: any, record: Product) => record.variants?.length || 0,
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  const resourceTypeMap: Record<string, string> = {
    consumable: t('executionResources.typeConsumable'),
    tool: t('executionResources.typeTool'),
    power_tool: t('executionResources.typePowerTool'),
    vehicle: t('executionResources.typeVehicle'),
    man_power: t('executionResources.typeManPower'),
  };

  const resourceColumns = [
    {
      title: t('executionResources.resourceNumber'),
      dataIndex: 'resource_number',
      key: 'resource_number',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('executionResources.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="blue">{resourceTypeMap[type] || type}</Tag>
      ),
    },
    {
      title: t('executionResources.pricingOptions'),
      key: 'pricing_options_count',
      render: (_: any, record: ExecutionResource) => record.pricing_options?.length || 0,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/service-providers')}>
          {t('common.back')}
        </Button>
      </Space>

      <Card title={t('serviceProviders.profile')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label={t('serviceProviders.logo')}>
            {sp.logo ? (
              <Image src={sp.logo} width={64} height={64} style={{ objectFit: 'contain' }} />
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('serviceProviders.name')}>{sp.name}</Descriptions.Item>
          <Descriptions.Item label={t('serviceProviders.tin')}>{sp.tin || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('serviceProviders.phone')}>{sp.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('common.createdAt')}>
            {formatDate(sp.created_at, i18n.language)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('serviceProviders.admins')} style={{ marginBottom: 16 }}>
        <Table
          columns={adminColumns}
          dataSource={sp.admins || []}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('serviceProviders.noAdmins') }}
        />
      </Card>

      <Card title={t('serviceProviders.products')} style={{ marginBottom: 16 }}>
        <Table
          columns={productColumns}
          dataSource={sp.products || []}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
        />
      </Card>

      <Card title={t('menu.executionResources')}>
        <Table
          columns={resourceColumns}
          dataSource={executionResources}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
        />
      </Card>
    </div>
  );
};

export default ServiceProviderDetail;
